/* 
This React component provides a password reset functionality. It uses the `Formik` library for form handling, including validation and submission. The `useLocation` hook from `react-router-dom` is used to extract the password reset token from the URL.

The component maintains several pieces of state:
- `resetData`: Stores the result of the password reset request.
- `resetMessage`: Holds the message to display based on the reset request's outcome.
- `passwordsMatch`: A boolean indicating whether the password and its confirmation match.

The `useFormik` hook initializes the form with two fields: `password` and `passwordConfirm`. It handles form validation, ensuring both fields are filled and match each other. On form submission, it sends a request to reset the password using the `getResetPassword` function, which communicates with the backend.

The `useEffect` hook updates the `passwordsMatch` state whenever the password or its confirmation changes.

The component renders a form with:
- A password input field (`RegisterPassword`) for entering the new password.
- A confirmation password input field (`ConfirmationPasswordInput`) for re-entering the password.
- Error messages and success notifications are displayed based on the form's validation and submission result.

Styling is applied using `styled-components`, including layout and error message styles
*/
import React,{ useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import { useTranslation } from "react-i18next";
import styled from 'styled-components';
import { GeneralButton, RegisterPassword } from '../../components';
import { PasswordInput } from "@mantine/core";
import { getResetPassword } from '../../services/identity/resetPasswordService';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Reset = () => {

  const [resetData, setResetData] = useState([]);
  const [resetMessage, setResetMessage] = useState([]);
  const [passwordsMatch, setPasswordsMatch] = useState(false);

  const { t } = useTranslation();

  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter((segment) => segment);
  const token = pathSegments[1];

  const formik = useFormik({
    initialValues: {
      password: '',
      passwordConfirm: '',
    },
    onSubmit: async (values) => {
      try {
        const { password, passwordConfirm } = values;

        const res = await getResetPassword(token, password, passwordConfirm);
        setResetData(res?.data?.isSuccess || false);
        setResetMessage(res?.data?.message || "");

        if (res?.data?.isSuccess === false) {
          toast.error(resetMessage);
        } 
        if (res?.data?.isSuccess === true ) {
          toast.success(t("changedPassword"));
        }

        // console.log("Reset",res);

        // console.log('Password reset successful', token, password, passwordConfirm);
      } catch (error) {
        console.error('Password reset failed', error);
      }
    },
    validate: (values) => {
      const errors = {};

      if (!values.password) {
        errors.password = 'Password is required';
      }
      if (!values.passwordConfirm) {
        errors.passwordConfirm = 'Password confirmation is required';
      } 
      else if (values.password !== values.passwordConfirm) {
        errors.passwordConfirm = t("passwordsDontMatch");
      }
      
      return errors;
    },
  });

  useEffect(() => {

    setPasswordsMatch(formik.values.password === formik.values.passwordConfirm);

  }, [formik.values.password, formik.values.passwordConfirm]);

  return (
    <>
      <ToastContainer/>
      <ResetMain>
        <ResetContainer>
          <ResetTitle>{t("resetPassword")}</ResetTitle>
          <form onSubmit={formik.handleSubmit}>
            <div>
                <RegisterPassword
                  handleChange={formik.handleChange}                   
                  value={formik.values.password}
                />
              {formik.touched.password && formik.errors.password && 
              <div style={{color: "red", fontSize: "13px", marginLeft: "5px"}}>{formik.errors.password}</div>
              }
            </div>
            <div>
              <ConfirmationPasswordInput  
                id="passwordConfirm" 
                name="passwordConfirm"
                placeholder={t("confirmPassword")}
                onChange={formik.handleChange}
                // onBlur={formik.handleBlur}
                value={formik.values.passwordConfirm}
                size="lg"
                my="lg"
                error={passwordsMatch ? (
                  ""
                ) : (
                  <ErrorMessage>{t("passwordsDontMatch")}</ErrorMessage>
              )}
              />
              {formik.touched.passwordConfirm && formik.errors.passwordConfirm && (
                <ErrorMessage>{t("passwordsDontMatch")}</ErrorMessage>
              )}
            </div>
            <div style={{color: "red", fontSize: "16px", marginLeft: "5px", marginBottom: "20px"}}>{resetMessage}</div>      
            <GeneralButton 
              type="submit"
              width="100%"
              margin="20px 0px"
            >
              {t("resetPassword")}
            </GeneralButton>
          </form>
        </ResetContainer>
      </ResetMain>
    </>
  )
}

export default Reset

export const ResetMain = styled.main`
  max-width: 1100px;
  margin: 100px auto;
`;
export const ResetContainer = styled.div`
  max-width: 400px;
  padding: 20px;
  border-radius: 8px;
  margin: 50px auto;
  box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
`;
export const ResetTitle = styled.h2`
  text-align: center;
`;

export const ConfirmationPasswordInput = styled(PasswordInput)`
  input::placeholder {
    font-size: 14px;
    color: #999; 
  }
`;

export const ErrorMessage = styled.p`
  margin: 5px 0;
  font-size: 14px;
  color: red;
`;